import React from 'react';

// ─── Unified scenario-figure style ───────────────────────────────────────────
// The reference implementation is the Deal Implementation Timeline
// (timeline/TimelineLineDiagram.tsx): all chart text in the site serif
// (ET Book), near-black ink for axes/structural marks, a single dark-red
// accent, no frames or top titles — each figure instead carries a
// bottom-right caption (italic serif figure title + mono site attribution).

// STRICTLY TWO-TONE ("two-tone printer" / cold-war-report theme): every mark
// is either ink or red at full strength — no grays, no pink tints. Where a
// lighter shade is needed, use the dither/hatch patterns below instead
// (TwoTonePatterns), so intermediate tones read as print artifacts.
export const FIG_INK = '#111111';   // axes + structural marks
export const FIG_TEXT = '#1a1a1a';  // labels + values
export const FIG_RED = '#8b0000';   // the single accent (markers, data ink)
export const FIG_WARM = '#fffff8';  // paper — knockout text/separators on solid fills

// For SVG fontFamily attributes (HTML styling should use Tailwind arbitrary
// font classes instead, per the styling conventions).
export const FIG_SERIF = 'et-book, Palatino, "Palatino Linotype", "Book Antiqua", Georgia, serif';
export const FIG_MONO = 'Menlo, Consolas, "Liberation Mono", "DejaVu Sans Mono", monospace';

export const FIG_ATTRIBUTION = ' - ai-2040.com';

// Print-style tone patterns for the two-tone theme — INK-DOMINANT: a solid
// ink/red field with thin paper gaps knocked out as 45° bars (both inks run
// the same direction), so a "lighter" tone still reads as heavy print
// coverage rather than sparse line-work. `-bars-wide` has wider paper gaps
// for a second, lighter step when one ink needs two tones.
// Render once inside an <svg> (`<TwoTonePatterns prefix="myfig" />`) and
// fill with `url(#myfig-red-bars)` etc.
export function TwoTonePatterns({ prefix }: { prefix: string }) {
  const bar = (id: string, color: string, gap: number) => (
    <pattern
      id={`${prefix}-${id}`} width={9} height={9}
      patternUnits="userSpaceOnUse" patternTransform="rotate(45)"
    >
      <rect width={9} height={9} fill={color} />
      <line x1={0} y1={0} x2={0} y2={9} stroke={FIG_WARM} strokeWidth={gap} />
    </pattern>
  );
  // `-cross`: paper gaps in BOTH 45° directions (a waffle of ink squares) —
  // a third texture for when two same-direction densities are too close to
  // tell apart.
  const cross = (id: string, color: string, gap: number) => (
    <pattern
      id={`${prefix}-${id}`} width={9} height={9}
      patternUnits="userSpaceOnUse" patternTransform="rotate(45)"
    >
      <rect width={9} height={9} fill={color} />
      <line x1={0} y1={0} x2={0} y2={9} stroke={FIG_WARM} strokeWidth={gap} />
      <line x1={0} y1={0} x2={9} y2={0} stroke={FIG_WARM} strokeWidth={gap} />
    </pattern>
  );
  // PAPER-DOMINANT `-light` variants for large area fills where the
  // ink-dominant hatches overwhelm: mostly paper, thin tone hairlines.
  // `-light` = 45° stripes, `-light-135` = the opposite diagonal,
  // `-light-h` = horizontal, `-light-cross` = a fine open waffle.
  const light = (id: string, color: string, transform?: string) => (
    <pattern
      id={`${prefix}-${id}`} width={9} height={9}
      patternUnits="userSpaceOnUse" patternTransform={transform}
    >
      <rect width={9} height={9} fill={FIG_WARM} />
      <line x1={0} y1={0} x2={0} y2={9} stroke={color} strokeWidth={1} />
    </pattern>
  );
  const lightCross = (id: string, color: string) => (
    <pattern
      id={`${prefix}-${id}`} width={9} height={9}
      patternUnits="userSpaceOnUse" patternTransform="rotate(45)"
    >
      <rect width={9} height={9} fill={FIG_WARM} />
      <line x1={0} y1={0} x2={0} y2={9} stroke={color} strokeWidth={1} />
      <line x1={0} y1={0} x2={9} y2={0} stroke={color} strokeWidth={1} />
    </pattern>
  );
  return (
    <defs>
      {bar('ink-bars', FIG_INK, 1.8)}
      {bar('red-bars', FIG_RED, 1.8)}
      {bar('ink-bars-wide', FIG_INK, 3.6)}
      {bar('red-bars-wide', FIG_RED, 3.6)}
      {cross('ink-cross', FIG_INK, 1.8)}
      {cross('red-cross', FIG_RED, 1.8)}
      {light('ink-light', FIG_INK, 'rotate(45)')}
      {light('red-light', FIG_RED, 'rotate(45)')}
      {light('ink-light-135', FIG_INK, 'rotate(-45)')}
      {light('red-light-135', FIG_RED, 'rotate(-45)')}
      {light('ink-light-h', FIG_INK, 'rotate(90)')}
      {light('red-light-h', FIG_RED, 'rotate(90)')}
      {lightCross('ink-light-cross', FIG_INK)}
      {lightCross('red-light-cross', FIG_RED)}
    </defs>
  );
}

// HTML-legend equivalents of the `-light` patterns (CSS can't reference SVG
// patterns). angle 45/135 = the diagonals, 0 = horizontal lines.
export const lightCss = (tone: string, angle: 45 | 135 | 0 = 45) =>
  `repeating-linear-gradient(${angle}deg, ${tone} 0px, ${tone} 1px, ${FIG_WARM} 1px, ${FIG_WARM} 8px)`;
export const lightCrossCss = (tone: string) =>
  `repeating-linear-gradient(45deg, ${tone} 0px, ${tone} 1px, transparent 1px, transparent 8px), repeating-linear-gradient(135deg, ${tone} 0px, ${tone} 1px, transparent 1px, transparent 8px), ${FIG_WARM}`;

// MEDIUM-DENSITY hatch — the step between the paper-dominant `-light`s and
// the near-solid `-bars`: roughly 2:1 tone:paper diagonal stripes, so a
// patterned band still reads as clearly toned at a glance while the paper
// lines stay visible enough to tell it from the solid fill of the same hue.
// Parameterized by color so figures on the relaxed print palette (navy,
// amber, ...) can hatch their own hues — render inside the figure's <defs>
// and fill with `url(#your-id)`.
export function MediumHatchPattern({ id, color, angle = 45 }: { id: string; color: string; angle?: number }) {
  return (
    <pattern id={id} width={7} height={7} patternUnits="userSpaceOnUse" patternTransform={`rotate(${angle})`}>
      <rect width={7} height={7} fill={color} />
      <line x1={0} y1={0} x2={0} y2={7} stroke={FIG_WARM} strokeWidth={2.4} />
    </pattern>
  );
}
// HTML-legend equivalents of the medium hatch and of the ink-dominant
// `-cross` waffle (solid tone with paper lines in both diagonals).
export const mediumCss = (tone: string, angle: 45 | 135 | 0 = 45) =>
  `repeating-linear-gradient(${angle}deg, ${FIG_WARM} 0px, ${FIG_WARM} 2.4px, ${tone} 2.4px, ${tone} 7px)`;
export const crossCss = (tone: string) =>
  `repeating-linear-gradient(45deg, ${FIG_WARM} 0px, ${FIG_WARM} 1.8px, transparent 1.8px, transparent 9px), repeating-linear-gradient(135deg, ${FIG_WARM} 0px, ${FIG_WARM} 1.8px, transparent 1.8px, transparent 9px), ${tone}`;

// A stamped label tag: solid band-colored rect, thin paper border, knockout
// text. Keeps labels readable over patterned fills. Geometry constants match
// placeChipInBand below.
export const CHIP_H = 19;
export const chipWidth = (text: string) => text.length * 6.4 + 18;
export function LabelChip({ x, y, text, base, fontSize = 12 }: {
  x: number; y: number; text: string; base: string; fontSize?: number;
}) {
  return (
    <g>
      <rect x={x} y={y - 13} width={chipWidth(text)} height={CHIP_H} fill={base} stroke={FIG_WARM} strokeWidth={1.5} />
      <text x={x + 9} y={y} fontSize={fontSize} fontWeight={700} fill={FIG_WARM}>{text}</text>
    </g>
  );
}

// Place a chip fully inside a stacked band. `lo`/`hi` are the band's
// cumulative boundary values (chart units), `toX`/`toY` map (index, value) to
// svg coords. Slides the chip across the plot, measures the free vertical
// slot over the chip's WHOLE width (boundaries slope, so the tightest point
// governs), requires a margin on both sides, and among fitting positions
// picks the one nearest `anchorFrac` (0..1 across the plot). Returns null
// when the chip cannot fit anywhere (caller falls back — e.g. halo text).
export function placeChipInBand(opts: {
  lo: number[]; hi: number[]; text: string;
  xAt: (frac: number) => number;    // plot-fraction -> svg x
  yAt: (value: number) => number;   // chart value -> svg y
  plotWidth: number; anchorFrac: number; margin?: number;
}): { x: number; y: number } | null {
  const { lo, hi, text, xAt, yAt, plotWidth, anchorFrac } = opts;
  const margin = opts.margin ?? 5;
  const w = chipWidth(text);
  const N = lo.length;
  const interp = (vals: number[], fi: number) => {
    const i0 = Math.max(0, Math.min(N - 1, Math.floor(fi)));
    const i1 = Math.max(0, Math.min(N - 1, Math.ceil(fi)));
    return vals[i0] * (1 - (fi - i0)) + vals[i1] * (fi - i0);
  };
  let best: { x: number; y: number; dist: number } | null = null;
  for (let step = 0; step <= 160; step++) {
    const fx = (step / 160) * (1 - w / plotWidth);
    const x = xAt(fx);
    const fi0 = fx * (N - 1);
    const fi1 = (fx + w / plotWidth) * (N - 1);
    let yTop = -Infinity, yBot = Infinity;
    for (let k = 0; k <= 12; k++) {
      const fi = fi0 + (fi1 - fi0) * (k / 12);
      yTop = Math.max(yTop, yAt(interp(hi, fi)));
      yBot = Math.min(yBot, yAt(interp(lo, fi)));
    }
    if (yBot - yTop >= CHIP_H + 2 * margin) {
      const dist = Math.abs(fx - anchorFrac);
      const cand = { x, y: (yTop + yBot) / 2 + 4.5, dist };
      if (!best || cand.dist < best.dist) best = cand;
    }
  }
  return best ? { x: best.x, y: best.y } : null;
}

// When a `:::caption` block directly follows a figure in the story markdown,
// the markdown renderer (app/common/Markdown.tsx) passes the rendered caption
// through this context so it can appear BETWEEN the chart and the
// title/attribution line — reading order: chart, caption, attribution.
// Figures that don't render <FigureCaption> never consume it, so the renderer
// only pairs a caption with figures that do.
export const FigureCaptionContext = React.createContext<React.ReactNode>(null);

// Bottom-right caption shared by every scenario figure.
export function FigureCaption({ title }: { title: string }) {
  const caption = React.useContext(FigureCaptionContext);
  return (
    <>
      {caption ? (
        /* `figure-caption` is a hook for print.css (captions print a step
           smaller than body text); it carries no on-site styling. */
        <div className="figure-caption mt-3 pr-6 font-['et-book',Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif] text-[#1a1a1a] [&_p]:text-[15.5px] [&_p]:leading-[1.45] [&_p]:my-1 [&_ol]:text-[14px] [&_ul]:text-[14px] [&_li]:text-[14px]">
          {caption}
        </div>
      ) : null}
      <div className="mt-3 text-right leading-none">
        <span className="italic text-[15px] text-[#222222] font-['et-book',Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif]">
          {title}
        </span>
        {/* nowrap: browsers treat the hyphen in "ai-2040.com" as a break
            opportunity, splitting the domain across lines. */}
        <span className="whitespace-nowrap text-[12.5px] text-[#333333] font-['Menlo',Consolas,'Liberation_Mono','DejaVu_Sans_Mono',monospace]">
          {FIG_ATTRIBUTION}
        </span>
      </div>
    </>
  );
}
